import React, { useMemo, useState } from 'react';
import { Header } from './components/Header';
import { TeamSelector } from './components/TeamSelector';
import { LeadStats } from './components/LeadStats';
import { DistributionSummary } from './components/DistributionSummary';
import { FilterPanel } from './components/FilterPanel';
import { MOCK_LEADS, MOCK_TEAMS, MOCK_CAMPAIGNS } from './data';
import { FilterRule, Lead } from './types';
function evaluateRule(lead: Lead, rule: FilterRule): boolean {
  const raw = (lead as any)[rule.field];
  if (raw == null) return false;
  const {
    operator,
    value
  } = rule;
  if (value === '' || value == null) return true;
  if (rule.field === 'createdAt') {
    const leadTime = new Date(raw).getTime();
    const compareTime = new Date(value).getTime();
    if (isNaN(compareTime)) return true;
    if (operator === 'before') return leadTime < compareTime;
    if (operator === 'after') return leadTime > compareTime;
    return true;
  }
  const leadVal = String(raw).toLowerCase();
  switch (operator) {
    case 'equals':
      return leadVal === value.toLowerCase();
    case 'not_equals':
      return leadVal !== value.toLowerCase();
    case 'contains':
      return leadVal.includes(value.toLowerCase());
    case 'not_contains':
      return !leadVal.includes(value.toLowerCase());
    case 'is_one_of':
      {
        const list = value.split(',').map(v => v.trim().toLowerCase()).filter(Boolean);
        if (list.length === 0) return true;
        return list.includes(leadVal);
      }
    case 'is_not_one_of':
      {
        const list = value.split(',').map(v => v.trim().toLowerCase()).filter(Boolean);
        if (list.length === 0) return true;
        return !list.includes(leadVal);
      }
    default:
      return true;
  }
}
export function App() {
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [teamCampaignMap, setTeamCampaignMap] = useState<Record<string, string>>({});
  const [isSending, setIsSending] = useState(false);
  const [hasSent, setHasSent] = useState(false);
  const [rules, setRules] = useState<FilterRule[]>([]);
  const handleToggleTeam = (teamId: string) => {
    if (hasSent) return;
    setSelectedTeamIds(prev => {
      if (prev.includes(teamId)) {
        // Also clear campaign mapping when deselecting
        setTeamCampaignMap(map => {
          const next = {
            ...map
          };
          delete next[teamId];
          return next;
        });
        return prev.filter(id => id !== teamId);
      }
      return [...prev, teamId];
    });
  };
  const handleSetCampaign = (teamId: string, campaignId: string) => {
    if (hasSent) return;
    setTeamCampaignMap(prev => ({
      ...prev,
      [teamId]: campaignId
    }));
  };
  const filteredLeads = useMemo(() => {
    if (rules.length === 0) return MOCK_LEADS;
    return MOCK_LEADS.filter(lead => rules.every(rule => evaluateRule(lead, rule)));
  }, [rules]);
  const perTeamCount = selectedTeamIds.length > 0 ? Math.floor(filteredLeads.length / selectedTeamIds.length) : null;
  const handleSendToBonzo = () => {
    setIsSending(true);
    setTimeout(() => {
      setIsSending(false);
      setHasSent(true);
    }, 2000);
  };
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-900" data-id="element-0">
      <Header data-id="element-1" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2">
        <div className="mb-8" data-id="element-3">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-4">
            Daily Lead Distribution
          </h1>
          <p className="text-slate-600 mt-1" data-id="element-5">
            Filter today's leads, assign them to teams and Bonzo campaigns, then
            sync.
          </p>
        </div>

        <div className="space-y-6" data-id="element-6">
          <FilterPanel rules={rules} onRulesChange={setRules} totalLeads={MOCK_LEADS.length} filteredCount={filteredLeads.length} data-id="element-7" />

          <TeamSelector teams={MOCK_TEAMS} campaigns={MOCK_CAMPAIGNS} selectedTeamIds={selectedTeamIds} teamCampaignMap={teamCampaignMap} onToggleTeam={handleToggleTeam} onSetCampaign={handleSetCampaign} perTeamCount={perTeamCount} data-id="element-8" />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-9">
            <div className="lg:col-span-2" data-id="element-10">
              <LeadStats leads={filteredLeads} data-id="element-11" />
            </div>
            <div data-id="element-12">
              <DistributionSummary teams={MOCK_TEAMS} campaigns={MOCK_CAMPAIGNS} selectedTeamIds={selectedTeamIds} teamCampaignMap={teamCampaignMap} totalLeads={filteredLeads.length} onSend={handleSendToBonzo} isSending={isSending} hasSent={hasSent} data-id="element-13" />
            </div>
          </div>
        </div>
      </main>
    </div>;
}